import { Injectable, inject } from "@angular/core";
import { Scheduler } from "@bryntum/scheduler-thin";
import { TicketStatus } from "@app/models/enums/ticket-status.enum";
import moment from "moment";
import { Ticket } from "@app/models/rest/ticket";
import {
  ClientConfigService,
  TicketColoursConfig
} from "@app/core/config/client-config.service";
import { SchedulerService } from "@app/view/planning/schedule-viewer/scheduler.service";

@Injectable({
  providedIn: "root"
})
export class TicketCompactRenderer {
  protected clientConfig = inject(ClientConfigService);
  protected schedulerService = inject(SchedulerService);

  public scheduler: Scheduler;
  _colors: TicketColoursConfig;

  constructor() {
    this._colors = this.clientConfig.config.ticketColours;
  }

  public static getTicketColour(status: TicketStatus): string {
    switch (status) {
      case TicketStatus.DESPATCHED:
        return "purple";
      case TicketStatus.PLANNING:
        return "gray";
      case TicketStatus.ENROUTE:
        return "violet";
      case TicketStatus.ARRIVED:
        return "indigo";
      case TicketStatus.STARTED:
        return "blue";
      case TicketStatus.COMPLETE:
        // if (ticket.followOnRequired) {
        //   if (ticket.followOnActioned) {
        //     return 'green';
        //   }
        //   return 'pink';
        // }
        return "green";
    }
    return "";
  }

  public static getTicketStatusText(ticket: Ticket): string {
    switch (ticket.ticketStatus) {
      case TicketStatus.DESPATCHED:
        return "Despatched";
      case TicketStatus.ARRIVED:
        return "Arrived";
      case TicketStatus.ENROUTE:
        return "En Route";
      case TicketStatus.COMPLETE:
        return "Complete";
      case TicketStatus.PAUSED:
        return "Paused";
      case TicketStatus.STARTED:
        return "Started";
      case TicketStatus.PINNED:
        return "Pinned";
    }
    return "Auto";
  }

  public renderEvent(display: {
    eventRecord: any;
    renderData: {
      left: number;
      style: string;
      eventStyle: string;
      wrapperStyle: string;
      height: number;
      width: number;
    };
  }) {
    // console.log('ab', this.scheduler, display);
    if (!this.schedulerService.scheduler.getValue()) {
      return;
    }

    // Force a compact event height
    display.renderData.height = 40;
    const dateToPx = (date) =>
      this.schedulerService.scheduler
        .getValue()
        .getCoordinateFromDate(moment(date).parseZone().toDate()) -
      Math.max(0, display.renderData.left);

    let warningIcons = "";
    let bottomText = "";
    let statusText;
    if (
      display.eventRecord.get("issues") &&
      !display.eventRecord.get("followOnRequired")
    ) {
      warningIcons = '<i class="b-fa b-fa-exclamation-triangle pulse"></i>';
    }
    if (display.eventRecord.get("followOnRequired")) {
      warningIcons += '<i class="b-fa b-fa-bell pulse"></i>';
    }
    if (display.eventRecord.get("recallPending")) {
      warningIcons +=
        '<i class="b-fa b-fa-rotate-left pulse" style="color: orangered;"></i>';
    }

    let topLeftIcons = "";
    let topRightIcons = "";
    let jobNumberDisplay = "";

    // console.log(display.eventRecord.get('forceStartTime'));
    let isMeeting = display.eventRecord.get("meetingId");
    if (!isMeeting) {
      if (
        display.eventRecord.get("forceStartTime") ||
        display.eventRecord.get("lockResourceId")
      ) {
        topLeftIcons = '<i class="b-fa b-fa-lock"></i>';
      }
      if (display.eventRecord.get("forceStartTime")) {
        topLeftIcons += '<i class="b-fa b-fa-clock"></i>';
      }
      if (display.eventRecord.get("lockResourceId")) {
        topLeftIcons += '<i class="b-fa b-fa-user"></i>';
      }
      if (display.eventRecord.get("minimumAttendance") > 1) {
        topLeftIcons +=
          "<i class=\"b-fa b-fa-exclamation-triangle pulse\" style='font-weight: bolder; color: #d81b60'></i>";
      }

      if (display.eventRecord.get("completionText")) {
        bottomText += display.eventRecord.get("completionText");
      }
      if (display.eventRecord.get("externalJobRef")) {
        jobNumberDisplay += display.eventRecord.get("externalJobRef");
      }

      if (display.eventRecord.get("groupedTicketId")) {
        topRightIcons += '<i class="b-fa b-fa-user-friends"></i>';
      }

      statusText = display.eventRecord.get("ticketStatus");
    } else {
      topLeftIcons += '<i class="fa-solid fa-calendar-circle-user"></i>';

      display.renderData.style = "background-color: #123456;color:#ffffff";
      jobNumberDisplay = "Meeting";
      if (display.eventRecord.get("meetingDescription")) {
        statusText = display.eventRecord.get("meetingDescription");
      } else {
        statusText = "MEETING";
      }
    }
    if (
      this._colors.status[statusText] &&
      this._colors.status[statusText].background !== ""
    ) {
      if (statusText === "PLANNING" && display.eventRecord.get("issues")) {
        display.renderData.style =
          "background-color:" +
          this._colors.status.PLANNINGISSUE.background +
          ";color:" +
          this._colors.status.PLANNINGISSUE.foreground;
      } else {
        if (display.eventRecord.get("completionCode")) {
          display.renderData.style =
            "background-color:" +
            display.eventRecord.get("completionBackground") +
            ";color:" +
            display.eventRecord.get("completionForeground");
        } else {
          display.renderData.style =
            "background-color:" +
            this._colors.status[statusText].background +
            ";color:" +
            this._colors.status[statusText].foreground;
        }
      }
    }

    display.renderData.style += ";border-radius: 10px;";

    if (display.eventRecord.get("followOnRequired")) {
      statusText = "FOLLOW ON";
    }

    // if (display.eventRecord.get('issues') && !display.eventRecord.get('followOnRequired')) {
    //   topRightIcons += '<i class="b-fa b-fa-exclamation-triangle"></i>' + display.eventRecord.get('issues').length;
    //
    // }

    //en route
    let enRouteDate = display.eventRecord.get("enrouteTime");
    if (enRouteDate < this.schedulerService.scheduler.getValue().startDate) {
      enRouteDate = this.schedulerService.scheduler.getValue().startDate;
      display.renderData.style += ";border-top-left-radius: 0;";
      display.renderData.style += ";border-bottom-left-radius: 0;";
    }
    if (enRouteDate > this.schedulerService.scheduler.getValue().endDate) {
      enRouteDate = this.schedulerService.scheduler.getValue().endDate;
    }

    //arrived date
    let arrivedDate = display.eventRecord.get("arrivedTime");
    if (arrivedDate < this.schedulerService.scheduler.getValue().startDate) {
      arrivedDate = this.schedulerService.scheduler.getValue().startDate;
    }
    if (arrivedDate > this.schedulerService.scheduler.getValue().endDate) {
      arrivedDate = this.schedulerService.scheduler.getValue().endDate;
    }

    let startedDate = display.eventRecord.get("startedTime");
    if (startedDate < this.schedulerService.scheduler.getValue().startDate) {
      startedDate = this.schedulerService.scheduler.getValue().startDate;
    }
    if (startedDate > this.schedulerService.scheduler.getValue().endDate) {
      startedDate = this.schedulerService.scheduler.getValue().endDate;
    }

    let completedDate = display.eventRecord.get("completedTime");
    if (completedDate < this.schedulerService.scheduler.getValue().startDate) {
      completedDate = this.schedulerService.scheduler.getValue().startDate;
    }
    if (completedDate > this.schedulerService.scheduler.getValue().endDate) {
      completedDate = this.schedulerService.scheduler.getValue().endDate;
      display.renderData.style += ";border-top-right-radius: 0;";
      display.renderData.style += ";border-bottom-right-radius: 0;";
    }

    let enRoute = dateToPx(enRouteDate);
    let arrived = dateToPx(arrivedDate);
    let started = dateToPx(startedDate);
    let completed = dateToPx(completedDate);

    // console.log(jobNumberDisplay + " render range: ", this.schedulerService.scheduler.getValue().startDate, this.schedulerService.scheduler.getValue().endDate);
    //log each date and pixel position for enRoute, arrived, started, completed,
    // console.log("enRoute", enRoute, enRouteDate, display.eventRecord.get("enrouteTime"), display.renderData.left);
    // console.log("arrived", arrived, arrivedDate, display.eventRecord.get("arrivedTime"));
    // console.log("started", started, startedDate, display.eventRecord.get("startedTime"));
    // console.log("completed", completed, completedDate, display.eventRecord.get("completedTime"));

    //
    // console.log("en route", enRoute,display.eventRecord.get("enrouteTime"), minStart, this.schedulerService.scheduler.getValue().startDate );
    // if (enRoute < 0) {
    //   enRoute = maxEnd;
    // }
    // if (arrived < 0) {
    //   arrived = maxEnd;
    // }
    // if (started < 0) {
    //   started = maxEnd;
    // }
    // if (completed < 0) {
    //   completed = maxEnd;
    // }
    let totalWidth = completed - enRoute;

    let barHeight = "13px";

    return `
      <div class="ticket-event" style="height: 40px; width: ${
        totalWidth + "px"
      }; display: flex; align-items: center; padding: 12px 4px 12px 4px; box-sizing: border-box; overflow: hidden; position: relative;">
        <div style="position: absolute; left: 0; right: 0; top: 0;
  height: ${barHeight}; line-height: ${barHeight};
  padding-left: 5px; display: flex; align-items: center; background-color: ${display.eventRecord.get(
    "priorityBackground"
  )};
  color: ${display.eventRecord.get("priorityForeground")};">
          <span style="font-size: 10px; line-height: 1; display: inline-flex; gap: 2px; align-items: center;">
          ${topLeftIcons}
          </span>
          <span style="flex: 1"></span>
          <span style="max-width:${
            totalWidth + "px"
          }; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; font-size: 11px; line-height: ${barHeight};">
          ${jobNumberDisplay}
          </span>
          <span style="flex: 1"></span>
          <span style="font-size: 10px; line-height: 1; display: inline-flex; gap: 2px; align-items: center;">
          ${topRightIcons}
          </span>
        </div>
        <div style="position: absolute; height: ${barHeight};
          bottom: 0; display: flex; background-color: ${
            this._colors.timings.travel
          }; color: ${this._colors.timings.textColour}; left: ${
            enRoute + "px"
          }; width: ${arrived + "px"};"></div>
        <div style="position: absolute; height: ${barHeight};
          bottom: 0; display: flex; background-color: ${
            this._colors.timings.wait
          }; color: ${this._colors.timings.textColour}; left: ${
            arrived + "px"
          }; width: ${started - arrived + "px"};"></div>
        <div style="position: absolute; height: ${barHeight};
          bottom: 0; display: flex; background-color: ${
            this._colors.timings.work
          }; color: ${this._colors.timings.textColour}; left: ${
            started + "px"
          }; width: ${completed - started + "px"};"></div>
        <span class="ticket-event-icons" style="font-size: 12px; margin-right: 4px; display: inline-flex; gap: 2px; align-items: center;">${warningIcons}</span>
        <span class="ticket-event-status" style="font-size: 12px; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${statusText}</span>
        <div style="position: absolute; left: 0; right: 0; bottom: 0;
        height: ${barHeight}; line-height: ${barHeight};
        padding-left: 2px; display: flex; align-items: center; background-color: transparent; color: white; font-size: 9px; justify-content: center;">
            ${bottomText}
        </div>
      </div>
    `;
  }
}
